import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Image as ImageIcon, Minus, Maximize2, Minimize2 } from 'lucide-react';
import { getTranslator } from './translations.js';

/** nginx sert le SPA sur /uploads — les fichiers Node sont sur /api/uploads */
export function resolvePublicUploadUrl(raw, backendUrl = '') {
  try {
    let s = String(raw || '');
    if (!s) return '';
    if (s.startsWith('blob:')) return s;
    let pathname = s;
    let search = '';
    if (/^https?:\/\//i.test(s)) {
      const u = new URL(s);
      pathname = u.pathname || '/';
      search = u.search || '';
    } else if (!pathname.startsWith('/')) {
      pathname = `/${pathname}`;
    }
    pathname = pathname.replace(/^\/api\/uploads\//i, '/uploads/');
    if (pathname.startsWith('/uploads/')) pathname = `/api${pathname}`;
    const be = String(backendUrl || '').replace(/\/$/, '').replace(/\/api$/i, '');
    if (be && !/localhost|127\.0\.0\.1/i.test(be)) {
      try {
        const o = new URL(be.startsWith('http') ? be : `https://${be}`);
        return `${o.origin}${pathname}${search}`;
      } catch { /* */ }
    }
    return `https://global-strains.shop${pathname}${search}`;
  } catch {
    return '';
  }
}

// ── Barre de progression liquide bleue ───────────────────────────────────────
export function LiquidProgressBar({ progress = 0 }) {
  const safePct = Math.max(0, Math.min(100, Number(progress) || 0));
  return (
    <div
      className="w-full rounded-full overflow-hidden mt-2 mb-1"
      style={{
        height: 6,
        background: 'rgba(255,255,255,0.12)',
        border: '1px solid rgba(147,197,253,0.30)',
        position: 'relative',
        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.25)',
      }}
    >
      <div
        style={{
          width: `${safePct}%`,
          height: '100%',
          background: 'linear-gradient(90deg, rgba(125,211,252,0.95) 0%, rgba(59,130,246,0.95) 50%, rgba(37,99,235,0.95) 100%)',
          borderRadius: 999,
          boxShadow: '0 0 10px rgba(96,165,250,0.85), 0 0 20px rgba(59,130,246,0.45), inset 0 1px 0 rgba(255,255,255,0.55)',
          transition: 'width 0.22s cubic-bezier(.22,1,.36,1)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.55) 50%, transparent 100%)',
            animation: 'gsLiquidShine 1.2s linear infinite',
          }}
        />
      </div>
      <style>{`
        @keyframes gsLiquidShine {
          0% { transform: translateX(-120%); }
          100% { transform: translateX(120%); }
        }
      `}</style>
    </div>
  );
}

// ── ChatImage ────────────────────────────────────────────────────────────────
// Affiche un spinner pendant le chargement, un placeholder SVG en cas d'erreur,
// et tente une URL de fallback absolue avant de capituler.
// Inclut aussi la barre de progression quand uploading=true.
// Stratégie : l'<img> est TOUJOURS dans le DOM (jamais display:none) pour que
// onLoad/onError se déclenchent même si l'image était déjà en cache navigateur.
// Le skeleton est superposé par-dessus via position absolute et retiré à onLoad.
function ChatImage({ src, alt, onClick, uploading = false, uploadProgress = 0, fallbackBlob = null, backendUrl = '', messageId = null }) {
  const [phase, setPhase] = useState(uploading ? 'uploading' : 'loading'); // 'uploading' | 'loading' | 'loaded' | 'error'
  const imgRef = useRef(null);
  const retriedRef = useRef(0);
  const resolvedBackendRef = useRef(String(backendUrl || '').replace(/\/api\/?$/i, ''));

  const resolveFallbackPublic = (raw) =>
    resolvePublicUploadUrl(raw, resolvedBackendRef.current || backendUrl || '');

  const blobFromStore = () => {
    try {
      if (fallbackBlob) return fallbackBlob;
      if (!messageId) return null;
      const store = window.__gsChatBlobById;
      if (store instanceof Map) return store.get(String(messageId)) || null;
      return null;
    } catch { return null; }
  };

  // Normalise dès l'arrivée (évite 1er load sur une URL host cassée)
  const displaySrc = (() => {
    if (!src || String(src).startsWith('blob:')) return src;
    try {
      resolvedBackendRef.current = String(backendUrl || '').replace(/\/api\/?$/i, '');
      const rebuilt = resolveFallbackPublic(src);
      return rebuilt || src;
    } catch {
      return src;
    }
  })();

  useEffect(() => {
    retriedRef.current = 0;
    resolvedBackendRef.current = String(backendUrl || '').replace(/\/api\/?$/i, '');
    if (uploading) {
      setPhase('uploading');
      return;
    }
    setPhase('loading');
    requestAnimationFrame(() => {
      const el = imgRef.current;
      if (!el) return;
      if (el.complete && el.naturalWidth > 0) {
        setPhase('loaded');
      } else if (el.complete) {
        try { handleErrorInternal(); } catch { /* */ }
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displaySrc, uploading, backendUrl]);

  const handleLoad = () => {
    if (phase !== 'uploading') setPhase('loaded');
  };

  const handleErrorInternal = () => {
    const el = imgRef.current;
    const currentSrc = (el && el.src) || src || '';
    if (!el) { setPhase('error'); return; }

    if (retriedRef.current === 0) {
      const rebuilt = resolveFallbackPublic(currentSrc || src);
      if (rebuilt && rebuilt !== currentSrc) {
        retriedRef.current = 1;
        el.src = rebuilt;
        return;
      }
    }

    if (retriedRef.current === 0) {
      retriedRef.current = 1;
      try {
        const sep = currentSrc.includes('?') ? '&' : '?';
        el.src = `${currentSrc}${sep}r=${Date.now()}`;
        return;
      } catch { /* */ }
    }

    if (retriedRef.current === 1) {
      retriedRef.current = 2;
      const rebuilt = resolveFallbackPublic(currentSrc || src);
      if (rebuilt && rebuilt !== currentSrc) {
        el.src = rebuilt;
        return;
      }
    }

    if (retriedRef.current === 2) {
      const fb = blobFromStore();
      if (fb) {
        retriedRef.current = 3;
        try {
          el.src = fb;
          return;
        } catch { /* */ }
      }
    }

    setPhase('error');
  };

  const isLoading = phase === 'loading' || phase === 'uploading';
  const isError = phase === 'error';
  const isUploading = phase === 'uploading';

  return (
    <div
      className="rounded-xl mb-2 w-full overflow-hidden border border-white/20"
      style={{
        position: 'relative',
        background: 'rgba(0,0,0,0.20)',
        minHeight: 80,
        cursor: phase === 'loaded' ? 'zoom-in' : 'default',
      }}
      onClick={phase === 'loaded' ? onClick : undefined}
    >
      {/* L'image — TOUJOURS dans le DOM, invisible seulement via opacity pour garder les events */}
      <img
        ref={imgRef}
        src={displaySrc}
        alt={alt || 'Photo'}
        onLoad={handleLoad}
        onError={handleErrorInternal}
        referrerPolicy="no-referrer"
        style={{
          display: isError ? 'none' : 'block',
          width: '100%',
          maxHeight: '14rem',
          objectFit: 'contain',
          opacity: isLoading ? 0.35 : 1,
          transition: 'opacity 0.2s',
        }}
        className="hover:opacity-90"
      />

      {/* Overlay pendant loading / uploading */}
      {isLoading && (
        <div
          style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1, background: 'rgba(0,0,0,0.18)',
          }}
        >
          {isUploading ? (
            <div style={{
              width: '80%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 10,
            }}>
              <div style={{
                width: 34, height: 34,
                border: '3px solid rgba(147,197,253,0.30)',
                borderTop: '3px solid rgba(147,197,253,0.95)',
                borderRadius: '50%',
                animation: 'gsImgSpin 0.7s linear infinite',
              }} />
              <div style={{ width: '100%' }}>
                <LiquidProgressBar progress={uploadProgress} />
              </div>
              <span style={{
                fontSize: 11,
                color: 'rgba(219,234,254,0.95)',
                fontWeight: 700,
                textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                letterSpacing: '0.02em',
              }}>
                Téléversement… {Math.round(uploadProgress)}%
              </span>
            </div>
          ) : (
            <div style={{
              width: 28, height: 28,
              border: '3px solid rgba(147,197,253,0.30)',
              borderTop: '3px solid rgba(147,197,253,0.90)',
              borderRadius: '50%',
              animation: 'gsImgSpin 0.75s linear infinite',
            }}
          />
          )}
          <style>{`@keyframes gsImgSpin{to{transform:rotate(360deg)}}`}</style>
        </div>
      )}

      {/* Placeholder erreur */}
      {isError && (
        <div
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', gap: 4, height: 72,
            background: 'rgba(30,58,138,0.35)', color: 'rgba(255,255,255,0.45)',
            fontSize: 11,
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <path d="M3 16l5-5 4 4 3-3 6 6"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
          </svg>
          <span>Image indisponible</span>
        </div>
      )}
    </div>
  );
}

const formatChatTimestamp = (dateString) => {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

const FALLBACK_T = getTranslator('fr');
const DEFAULT_PHOTO_ACCEPT = ['image/*', 'video/*'].join(',');

export default function FloatingChatPanel({
  title,
  subtitle,
  isOpen,
  isMinimized,
  onClose,
  onMinimize,
  messages,
  inputValue,
  onInputChange,
  onSend,
  onPhotoUpload,
  photoInputId,
  unreadCount,
  accentColor = '#ffffff',
  role = 'client',
  sending = false,
  uploading = false,
  uploadProgress = 0,
  uploadingMessageId = null,
  placeholder = null,
  photoAccept = DEFAULT_PHOTO_ACCEPT,
  backendUrl = '',
}) {
  const t = FALLBACK_T;
  const resolvedPlaceholder = placeholder ?? t('writeMsgPlaceholder');
  const safeMessages = Array.isArray(messages) ? messages : [];
  const [previewImage, setPreviewImage] = useState(null);
  const [previewMeta, setPreviewMeta] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Hauteur réelle visible (clavier mobile inclus) — utilisée uniquement en plein écran
  const [viewport, setViewport] = useState({ height: null, top: 0 });
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setIsFullscreen(false);
      setPreviewImage(null);
      setPreviewMeta(null);
    }
  }, [isOpen]);

  // Verrouille le scroll de la page en arrière-plan pendant que le chat est ouvert
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // Plein écran : on suit la hauteur VISIBLE réelle (barre d'adresse mobile + clavier) pour que
  // la barre d'écriture reste TOUJOURS collée en bas de l'écran, même quand le clavier s'ouvre.
  useEffect(() => {
    if (!isOpen || !isFullscreen) {
      setViewport({ height: null, top: 0 });
      return;
    }
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return;
    const update = () => setViewport({ height: Math.round(vv.height), top: Math.round(vv.offsetTop) });
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [isOpen, isFullscreen]);

  // Toujours afficher le dernier message (ouverture, nouveau message, changement de taille)
  useEffect(() => {
    if (!isOpen || isMinimized) return;
    const id = requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ block: 'end' });
    });
    return () => cancelAnimationFrame(id);
  }, [isOpen, isMinimized, isFullscreen, safeMessages.length, viewport.height]);

  // Au-dessus des locks Access / PWA (--gs-z-lock: 4000) — sinon le chat s'ouvre derrière le modal
  const CHAT_Z_INDEX = 4500;

  const handleClose = () => {
    setIsFullscreen(false);
    setPreviewImage(null);
    setPreviewMeta(null);
    onClose();
  };

  const handleMinimize = () => {
    setIsFullscreen(false);
    onMinimize();
  };

  const handleToggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  const handleImagePreview = (url, meta) => {
    setPreviewImage(url);
    setPreviewMeta(meta);
  };

  // ── Style liquid glass bleu transparent (même langage visuel que le reste de l'application) ──
  const glassPanelBackground =
    'linear-gradient(145deg, rgba(96,165,250,0.30) 0%, rgba(37,99,235,0.24) 45%, rgba(29,78,216,0.32) 100%), rgba(8,18,52,0.62)';
  const glassBorder = '1.5px solid rgba(147,197,253,0.40)';

  // Positionnement : en plein écran le panneau est un vrai conteneur fixe plein viewport
  // (header en haut / messages au milieu / barre d'écriture en bas), sinon bottom-sheet / carte flottante.
  const panelPositionStyle = isFullscreen
    ? {
        position: 'fixed',
        top: viewport.top || 0,
        left: 0,
        right: 0,
        width: '100%',
        height: viewport.height ? `${viewport.height}px` : '100dvh',
        borderRadius: 0,
      }
    : {};

  const panelClasses = isFullscreen
    ? ''
    : 'fixed inset-x-0 bottom-0 sm:inset-x-auto sm:bottom-4 sm:right-4 w-full sm:max-w-sm rounded-t-3xl sm:rounded-3xl';

  const panel = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Fond : isole visuellement le chat du reste de l'interface */}
          <motion.div
            key="chat-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={handleMinimize}
            style={{
              position: 'fixed',
              top: 0, left: 0, right: 0, bottom: 0,
              zIndex: CHAT_Z_INDEX - 1,
              background: 'radial-gradient(circle at 50% 100%, rgba(30,80,200,0.25) 0%, rgba(4,8,24,0.62) 70%)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
            }}
          />
          <motion.div
            key="chat-panel"
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            className={`${panelClasses} overflow-hidden flex flex-col`}
            style={{
              zIndex: CHAT_Z_INDEX,
              background: glassPanelBackground,
              backdropFilter: 'blur(40px) saturate(300%) brightness(1.08)',
              WebkitBackdropFilter: 'blur(40px) saturate(300%) brightness(1.08)',
              border: isFullscreen ? 'none' : glassBorder,
              boxShadow: isFullscreen
                ? 'none'
                : '0 20px 60px rgba(10,30,100,0.55), 0 0 50px rgba(74,144,226,0.25), inset 0 1.5px 0 rgba(255,255,255,0.35)',
              boxSizing: 'border-box',
              ...(isFullscreen
                ? panelPositionStyle
                : {
                    height: isMinimized ? 'auto' : 'min(80dvh, 620px)',
                    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                  }),
              ...(isFullscreen ? { paddingTop: 'env(safe-area-inset-top, 0px)' } : {}),
            }}
          >
            {/* Reflet supérieur liquid glass */}
            <div style={{ position: 'absolute', top: 0, left: '8%', right: '8%', height: 1.5, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.75) 50%, transparent)', pointerEvents: 'none', zIndex: 2 }} />

            {/* ── En-tête ── */}
            <div
              className="flex items-start justify-between p-3 sm:p-4 flex-shrink-0"
              style={{ position: 'relative', zIndex: 1, borderBottom: '1px solid rgba(147,197,253,0.25)', background: 'rgba(255,255,255,0.05)' }}
            >
              <div className="flex-1 min-w-0 pr-2">
                <p className="text-xs sm:text-sm uppercase tracking-wide truncate" style={{ color: 'rgba(191,219,254,0.85)' }}>{subtitle}</p>
                <h4 className="text-base sm:text-lg font-bold text-white truncate">{title}</h4>
                <p className="text-[10px] sm:text-xs mt-1" style={{ color: 'rgba(255,255,255,0.6)' }}>{(role === 'driver' || role === 'admin') ? t('chatDriver') : t('chatClient')}</p>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                {[
                  { onClick: handleToggleFullscreen, label: isFullscreen ? t('reduceWindow') : t('expandFullscreen'), icon: isFullscreen ? Minimize2 : Maximize2 },
                  { onClick: handleMinimize, label: t('reduceChat'), icon: Minus },
                  { onClick: handleClose, label: t('closeChat'), icon: X },
                ].map(({ onClick, label, icon: Icon }) => (
                  <button
                    key={label}
                    onClick={onClick}
                    className="p-2.5 sm:p-2 rounded-xl transition touch-manipulation"
                    aria-label={label}
                    style={{
                      background: 'rgba(255,255,255,0.12)',
                      border: '1px solid rgba(255,255,255,0.22)',
                      backdropFilter: 'blur(12px)',
                      WebkitBackdropFilter: 'blur(12px)',
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.25)',
                    }}
                  >
                    <Icon className="w-5 h-5 sm:w-4 sm:h-4" style={{ color: 'rgba(255,255,255,0.9)' }} />
                  </button>
                ))}
              </div>
            </div>

            {isMinimized ? (
              <div className="flex items-center justify-between p-3 sm:p-4 text-sm sm:text-base text-white/80">
                <span className="truncate flex-1 mr-2">{t('chatMinimized')}</span>
                {unreadCount > 0 && (
                  <span
                    className="px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-bold flex-shrink-0"
                    style={{ background: 'rgba(96,165,250,0.85)', color: '#fff', border: '1px solid rgba(191,219,254,0.6)' }}
                  >
                    {unreadCount} message{unreadCount > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            ) : (
              <>
                {/* ── Messages (zone qui défile) ── */}
                <div
                  className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-4 space-y-3 custom-scrollbar"
                  style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}
                >
                  {safeMessages.length === 0 ? (
                    <div className="text-center text-white/70 text-sm sm:text-base py-6">{t('noMessages')}</div>
                  ) : (
                    safeMessages.map((message, index) => {
                      const isClient = message.role === 'client' || message.role === 'customer';
                      const isSelf = (role === 'client' && isClient) || ((role === 'driver' || role === 'admin') && !isClient);
                      const isMsgUploading = !!uploadingMessageId && uploadingMessageId === message.id;
                      return (
                        <div key={message.id || index} className={`flex ${isSelf ? 'justify-end' : 'justify-start'}`}>
                          <div
                            className={`max-w-[85%] sm:max-w-[90%] rounded-2xl px-3 py-2.5 sm:px-4 sm:py-3 ${
                              isSelf ? 'rounded-br-sm' : 'rounded-bl-sm'
                            }`}
                            style={{
                              background: isSelf
                                ? 'linear-gradient(135deg, rgba(96,165,250,0.62) 0%, rgba(37,99,235,0.55) 100%)'
                                : 'linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.08) 100%)',
                              color: '#ffffff',
                              border: isSelf ? '1px solid rgba(191,219,254,0.55)' : '1px solid rgba(255,255,255,0.25)',
                              backdropFilter: 'blur(14px) saturate(200%)',
                              WebkitBackdropFilter: 'blur(14px) saturate(200%)',
                              boxShadow: isSelf
                                ? '0 6px 18px rgba(37,99,235,0.35), inset 0 1px 0 rgba(255,255,255,0.35)'
                                : '0 4px 14px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.22)',
                            }}
                          >
                            {(message.type === 'image' || message.mediaType === 'image') && (message.url || message.mediaUrl) ? (
                              <ChatImage
                                src={message.url || message.mediaUrl}
                                alt={t('attachment')}
                                uploading={isMsgUploading}
                                uploadProgress={isMsgUploading ? uploadProgress : 0}
                                messageId={message.id || message._id || null}
                                backendUrl={backendUrl || ''}
                                onClick={() => {
                                  if (isMsgUploading) return;
                                  const raw = message.url || message.mediaUrl;
                                  let preview = raw;
                                  try {
                                    preview = resolvePublicUploadUrl(raw, backendUrl || '') || raw;
                                  } catch { /* */ }
                                  handleImagePreview(preview, {
                                    author: message.authorName,
                                    date: formatChatTimestamp(message.createdAt),
                                  });
                                }}
                              />
                            ) : message.type === 'video' && message.url ? (
                              <video
                                controls
                                className="rounded-xl mb-2 w-full max-h-48 sm:max-h-56 border border-white/20 bg-black/20"
                                playsInline
                                preload="metadata"
                              >
                                {message.url.endsWith('.mp4') && (
                                  <source src={message.url} type="video/mp4" />
                                )}
                                {message.url.endsWith('.mov') && (
                                  <source src={message.url} type="video/quicktime" />
                                )}
                                {!message.url.endsWith('.mp4') && !message.url.endsWith('.mov') && (
                                  <source src={message.url} type="video/mp4" />
                                )}
                              </video>
                            ) : (
                              <p className="text-sm sm:text-base whitespace-pre-line break-words">{message.content}</p>
                            )}
                            <p className="text-[10px] sm:text-xs mt-2" style={{ color: 'rgba(255,255,255,0.68)' }}>
                              {message.authorName ? `${message.authorName} • ` : ''}
                              {formatChatTimestamp(message.createdAt)}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* ── Barre d'écriture : toujours en bas du panneau (flex-shrink-0) ── */}
                <div
                  className="p-3 sm:p-4 flex-shrink-0"
                  style={{
                    borderTop: '1px solid rgba(147,197,253,0.25)',
                    background: 'rgba(255,255,255,0.06)',
                    paddingBottom: isFullscreen ? 'max(12px, env(safe-area-inset-bottom, 0px))' : undefined,
                  }}
                >
                  <div className="flex items-end gap-2">
                    <label
                      htmlFor={photoInputId}
                      className={`p-2.5 sm:p-3 rounded-2xl cursor-pointer transition touch-manipulation flex-shrink-0 ${uploading ? 'opacity-60 cursor-not-allowed' : ''}`}
                      title={t('sendPhotoVideo')}
                      style={{
                        background: 'rgba(255,255,255,0.10)',
                        border: '1px solid rgba(147,197,253,0.35)',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                      }}
                    >
                      <ImageIcon className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: 'rgba(219,234,254,0.95)' }} />
                      <input
                        id={photoInputId}
                        type="file"
                        accept={photoAccept}
                        className="hidden"
                        onChange={onPhotoUpload}
                        disabled={uploading}
                      />
                    </label>
                    <textarea
                      value={inputValue}
                      onChange={(e) => onInputChange(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          onSend();
                        }
                      }}
                      placeholder={resolvedPlaceholder}
                      rows={1}
                      className="flex-1 resize-none rounded-2xl text-white px-3 py-2.5 sm:px-4 sm:py-3 text-sm sm:text-base focus:outline-none min-h-[44px] sm:min-h-[48px] placeholder-white/50"
                      style={{
                        maxHeight: '120px',
                        background: 'rgba(255,255,255,0.09)',
                        border: '1px solid rgba(147,197,253,0.35)',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.18)',
                        fontSize: 16, // évite le zoom automatique sur iOS
                      }}
                    />
                    <button
                      onClick={onSend}
                      disabled={sending || !inputValue.trim()}
                      className="p-2.5 sm:p-3 rounded-2xl font-bold disabled:opacity-50 disabled:cursor-not-allowed transition touch-manipulation flex-shrink-0"
                      style={{
                        background: 'linear-gradient(135deg, rgba(96,165,250,0.75) 0%, rgba(37,99,235,0.70) 100%)',
                        border: '1.5px solid rgba(191,219,254,0.6)',
                        color: '#fff',
                        boxShadow: '0 6px 18px rgba(37,99,235,0.45), inset 0 1px 0 rgba(255,255,255,0.4)',
                      }}
                    >
                      <Send className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>
                  </div>
                  {(uploading || sending) && (
                    <div className="mt-2">
                      {uploading ? (
                        <>
                          <LiquidProgressBar progress={uploadProgress} />
                          <p className="text-xs sm:text-sm text-white/70 mt-1 text-center font-medium">
                            {t('uploadingPhoto')} {Math.round(uploadProgress)}%
                          </p>
                        </>
                      ) : (
                        <p className="text-xs sm:text-sm text-white/70 text-center">
                          {t('sendingMessage')}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </motion.div>
            {previewImage && (
              <div
                className="bg-black/90 backdrop-blur-sm flex flex-col items-center justify-center p-3 sm:p-4"
                style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: CHAT_Z_INDEX + 1 }}
                onClick={() => {
                  setPreviewImage(null);
                  setPreviewMeta(null);
                }}
              >
                <div className="relative w-full max-w-5xl max-h-[90vh] flex flex-col">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewImage(null);
                      setPreviewMeta(null);
                    }}
                    className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4 bg-white/10 hover:bg-white/20 active:bg-white/30 text-white p-2.5 sm:p-2 rounded-full border border-white/20 touch-manipulation"
                    aria-label={t('closePreview')}
                  >
                    <X className="w-6 h-6 sm:w-5 sm:h-5" />
                  </button>
                  <img
                    src={previewImage}
                    alt={t('preview')}
                    className="w-full h-full object-contain rounded-2xl border border-white/20 shadow-2xl bg-black"
                    onClick={(e) => e.stopPropagation()}
                  />
                  {previewMeta && (
                    <div className="mt-2 sm:mt-3 text-center text-xs sm:text-sm text-white/80 px-2">
                      {previewMeta.author && <span className="font-semibold">{previewMeta.author}</span>}
                      {previewMeta.author && previewMeta.date && <span className="mx-2">•</span>}
                      {previewMeta.date && <span>{previewMeta.date}</span>}
                    </div>
                  )}
                </div>
              </div>
            )}
        </>
      )}
    </AnimatePresence>
  );

  // Rendu dans <body> : le panneau n'est plus affecté par un parent transformé (framer-motion),
  // ce qui était la cause du mode grand écran cassé (barre d'écriture en haut, non responsive).
  return typeof document !== 'undefined' ? createPortal(panel, document.body) : panel;
}